<script setup lang="ts">
import { onBeforeUnmount, useTemplateRef } from 'vue'
import type { HTMLAttributes } from 'vue'
import { useEventListener } from '@vueuse/core'

import { NumberField } from '../../primitives/number-field'
import NumberFieldInputStyled from '../../primitives/number-field/NumberFieldInput.vue'
import type { InputSize } from '../../primitives/input'

/*
 * A money field: Reka's NumberField configured for amounts, speaking the plain
 * numeric string the app's forms hold.
 *
 * A pattern rather than a prop set repeated at each call site, because the
 * configuration is not cosmetic — three of these settings are the difference
 * between a field that keeps what the user typed and one that quietly changes
 * it, and getting them right per screen is not something to leave to memory.
 *
 *   step-snapping  Reka defaults `step` to 1 and `stepSnapping` to true, so
 *                  every committed value is snapped to a whole number:
 *                  1,000,000.55 became 1,000,001 and 0.50 became 1. Off here,
 *                  while `step` stays 1 so the arrow keys still nudge by one
 *                  unit rather than by a cent.
 *   format         Two fraction digits at most, grouped — and no currency
 *                  symbol, because the amount and the currency are separate
 *                  fields everywhere in this app. Not a prop: see FORMAT_OPTIONS.
 *   wheel          A scroll over a focused field must never rewrite an amount.
 *
 * The model is a number, because an amount is one — and `undefined` for empty,
 * never `0`: a purchase price nobody has entered is not a purchase price of
 * nothing, and a component that reported it as zero would have every caller
 * unable to tell the two apart. A form that holds its values as strings
 * converts at its own boundary, where it knows what its schema reads.
 */
const props = defineProps<{
  /** The amount, e.g. `1250000.55`. `undefined` when the field is empty. */
  modelValue?: number
  /** Lowest value accepted. Defaults to none — a balance may be negative. */
  min?: number
  max?: number
  placeholder?: string
  /** `id` of the input, so a caller's label can point at it. */
  id?: string
  disabled?: boolean
  size?: InputSize
  class?: HTMLAttributes['class']
}>()

/*
 * Attributes belong on the input, not on the frame around it: `aria-label`,
 * `aria-labelledby` and `aria-describedby` name the control the user types in,
 * and left on the wrapper they name a `div` nothing reads. Same reason
 * DSDateField puts them on its segment group.
 */
defineOptions({ inheritAttrs: false })

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | undefined): void
  (e: 'blur'): void
}>()

/*
 * Two fraction digits, not configurable. A `fractionDigits: 0` field does not
 * round what is typed — Reka builds its parser from these options, so a field
 * that allows no decimals rejects "12.9" outright and commits nothing at all.
 * Whole-unit callers can have the knob when one exists and that behaviour has
 * been decided on; guessing at it here would ship a setting that empties the
 * field.
 */
const FORMAT_OPTIONS: Intl.NumberFormatOptions = { maximumFractionDigits: 2 }

const handleChange = (next: number | null | undefined) => {
  /*
   * Reka clears to undefined and its own prop type admits null; NaN is what a
   * half-parsed entry can leave behind. All three mean the same thing here, and
   * they are normalised to one so a caller has a single emptiness to check.
   */
  const isEmpty = next === null || next === undefined || Number.isNaN(next)
  emit('update:modelValue', isEmpty ? undefined : next)
}

const inputRef = useTemplateRef<{ $el: HTMLInputElement }>('inputRef')

/*
 * A quick, deliberately loose read of whatever is on screen right now — not
 * Reka's own locale-aware parser (internal to NumberFieldRoot, not exposed).
 * It only has to be good enough to keep the form's own state from going
 * stale between keystrokes; Reka's `applyInputValue` still runs the precise
 * parse/clamp on blur or Enter exactly as before and is the value of record.
 * A wrong or missed live read here self-corrects the moment the field is
 * blurred — it can make the form briefly optimistic, never wrong for good.
 *
 * `undefined` means "not a number yet" — a partial the field itself would
 * also reject as incomplete ("-", a bare "."). Empty is handled by the
 * caller, not here: unlike those partials, an empty field is not "typing
 * in progress", it is the user's own answer, and DSInput clears its model
 * the instant the field does.
 */
const parseLiveAmount = (raw: string): number | undefined => {
  const parsed = Number(raw.replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : undefined
}

/*
 * Reka's NumberField only parses and *commits* what is typed on blur or
 * Enter — by design, so a half-typed "-" or "1," is never forced into a
 * number mid-entry. Left alone, that means a value typed and then abandoned
 * without blurring (closing the wizard, an Escape that skips focus handling,
 * switching steps or tabs) never reaches `modelValue` at all and is silently
 * gone — unlike DSInput, whose native `input` event keeps its v-model
 * current on every keystroke, clearing included.
 *
 * This listens on the underlying input directly, independent of Reka's own
 * `@input` (which only redraws its local display text), so every keystroke
 * that reads as a number, or empties the field, also reaches this field's
 * model immediately — the same as DSInput.
 */
useEventListener(() => inputRef.value?.$el, 'input', (event: Event) => {
  const raw = (event.target as HTMLInputElement).value

  if (!raw.trim()) {
    handleChange(undefined)
    return
  }

  const parsed = parseLiveAmount(raw)
  if (parsed !== undefined) handleChange(parsed)
})

/*
 * Belt and braces for the cases the live read above skips on purpose — an
 * abandoned partial ("-", a bare ".") or a field cleared to empty. Forcing a
 * blur here, right before this field leaves the DOM, runs Reka's own commit
 * synchronously while the field is still fully mounted — the same path a
 * real blur takes, just guaranteed to run first. A no-op if the field was
 * never focused or has nothing pending.
 */
onBeforeUnmount(() => {
  inputRef.value?.$el?.blur()
})
</script>

<template>
  <NumberField
    :model-value="modelValue"
    :min
    :max
    :step-snapping="false"
    :format-options="FORMAT_OPTIONS"
    :disabled
    disable-wheel-change
    @update:model-value="handleChange"
  >
    <NumberFieldInputStyled
      :id
      ref="inputRef"
      v-bind="$attrs"
      :size
      :placeholder
      :class="props.class"
      @blur="emit('blur')"
    />
  </NumberField>
</template>
